import { uuidFilename } from '../utils/uuidFilename'
import { SOUND_CLIP_ENDPOINTS } from './endpoints'
import type {
  ApiError,
  CreateSoundClipPayload,
  PaginatedResponse,
  SoundClip,
  SoundClipApi,
  SoundClipApiClient,
  SoundClipListParams,
  SoundClipListResult,
  UpdateSoundClipPayload,
} from './soundClipApi.types'

function buildFormData(
  payload: CreateSoundClipPayload | UpdateSoundClipPayload,
): FormData {
  const formData = new FormData()

  if ('pbx_id' in payload) {
    formData.append('pbx_id', String(payload.pbx_id))
  }

  if (payload.name !== undefined) {
    formData.append('name', payload.name)
  }

  if (payload.feature_code !== undefined) {
    formData.append('feature_code', String(payload.feature_code))
  }

  if (payload.audio_file) {
    formData.append('audio_file', uuidFilename(payload.audio_file))
  }

  return formData
}

export const createSoundClipApi = (
  client: SoundClipApiClient,
): SoundClipApi => ({
  async getSoundClips(
    params: SoundClipListParams,
  ): Promise<SoundClipListResult> {
    const url = SOUND_CLIP_ENDPOINTS.LIST
    const queryParams: Record<string, unknown> = { pbx_id: params.pbxId }
    if (params.limit !== undefined) queryParams.limit = params.limit
    if (params.offset !== undefined) queryParams.offset = params.offset
    if (params.search) queryParams.search = params.search
    if (params.ordering) {
      // Break ties by name so rows don't shift between pages.
      queryParams.ordering =
        params.ordering.replace(/^-/, '') === 'name'
          ? params.ordering
          : `${params.ordering},name`
    }

    const response = await client.get<PaginatedResponse<SoundClip>>(
      url,
      queryParams,
    )

    if (!response.ok) {
      throw new Error(
        (response.data as ApiError | undefined)?.message ??
          `Failed to fetch sound clips (${response.status ?? 'unknown'})`,
      )
    }

    return {
      results: response.data!.results,
      totalCount: response.data!.count,
    }
  },

  async getSoundClip(id: number): Promise<SoundClip> {
    const url = SOUND_CLIP_ENDPOINTS.DETAIL(id)
    const response = await client.get<SoundClip>(url)

    if (!response.ok) {
      throw new Error(
        (response.data as ApiError | undefined)?.message ??
          `Failed to fetch sound clip (${response.status ?? 'unknown'})`,
      )
    }

    return response.data as SoundClip
  },

  async createSoundClip(payload: CreateSoundClipPayload): Promise<SoundClip> {
    const url = SOUND_CLIP_ENDPOINTS.LIST
    const formData = buildFormData(payload)
    const response = await client.post<SoundClip>(url, formData)

    if (!response.ok) {
      throw new Error(
        (response.data as ApiError | undefined)?.message ??
          `Failed to create sound clip (${response.status ?? 'unknown'})`,
      )
    }

    return response.data as SoundClip
  },

  async updateSoundClip(
    id: number,
    payload: UpdateSoundClipPayload,
  ): Promise<SoundClip> {
    const url = SOUND_CLIP_ENDPOINTS.DETAIL(id)
    const formData = buildFormData(payload)
    const response = await client.patch<SoundClip>(url, formData)

    if (!response.ok) {
      throw new Error(
        (response.data as ApiError | undefined)?.message ??
          `Failed to update sound clip (${response.status ?? 'unknown'})`,
      )
    }

    return response.data as SoundClip
  },

  async deleteSoundClip(id: number): Promise<void> {
    const url = SOUND_CLIP_ENDPOINTS.DETAIL(id)
    const response = await client.delete<void>(url)

    if (!response.ok) {
      throw new Error(
        (response.data as ApiError | undefined)?.message ??
          `Failed to delete sound clip (${response.status ?? 'unknown'})`,
      )
    }
  },
})
